import React from "react";
import styled from "styled-components";
import { colors } from "../../../../utils";

/**
 * MediaFallback — a clean, consistent placeholder shown when a media asset
 * (image / video / audio / document) fails to load or is unavailable.
 *
 * Purely defensive and additive: blocks render this ONLY after an onError /
 * load-failure, so valid media is never affected. Renders an inline line-art
 * SVG icon (no external asset to also 404) plus a short label.
 *
 * Props:
 *   variant  — "image" | "video" | "audio" | "document" (icon shape)
 *   label    — short caption (defaults per variant)
 *   style    — forwarded to the outer container (so it inherits the block's
 *              borderRadius / border / width like the real media would)
 *   compact  — smaller padding/icon for inline/list contexts (audio/doc rows)
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ compact }) => (compact ? "6px" : "10px")};
  width: 100%;
  min-height: ${({ compact }) => (compact ? "64px" : "120px")};
  padding: ${({ compact }) => (compact ? "12px" : "24px")};
  box-sizing: border-box;
  background-color: ${colors.background};
  color: ${colors.darkGrey};
  border-radius: 8px;
`;

const Label = styled.span`
  font-family: "DM Sans", "Poppins", sans-serif;
  font-size: ${({ compact }) => (compact ? "11px" : "13px")};
  font-weight: 500;
  line-height: 1.3;
  color: ${colors.darkGrey};
  text-align: center;
`;

const DEFAULT_LABELS = {
  image: "Image unavailable",
  video: "Video unavailable",
  audio: "Audio unavailable",
  document: "Document unavailable",
};

// Inline line-art icons (stroke-only). Kept inline so a fallback never depends
// on another network asset that could itself fail.
const Icon = ({ variant, size }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: colors.darkGrey,
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false,
  };

  switch (variant) {
    case "video":
      return (
        <svg {...common}>
          <rect x="2.5" y="5" width="19" height="14" rx="2" />
          <path d="M10 9.5l5 2.5-5 2.5z" />
        </svg>
      );
    case "audio":
      return (
        <svg {...common}>
          <path d="M9 18V6l10-2v12" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="16" cy="16" r="3" />
        </svg>
      );
    case "document":
      return (
        <svg {...common}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 16h6" />
        </svg>
      );
    case "image":
    default:
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="8.5" cy="9.5" r="1.5" />
          <path d="M4 17l4.5-4.5 3 3L15 12l5 5" />
        </svg>
      );
  }
};

const MediaFallback = ({ variant = "image", label, style, compact = false }) => {
  const iconSize = compact ? 28 : 40;
  const text = label || DEFAULT_LABELS[variant] || DEFAULT_LABELS.image;

  return (
    <Container compact={compact} style={style} role="img" aria-label={text}>
      <Icon variant={variant} size={iconSize} />
      <Label compact={compact}>{text}</Label>
    </Container>
  );
};

export default MediaFallback;
